import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wallet, Eye, EyeOff, Mail, CheckCircle2 } from "lucide-react";

function GoogleIcon() {
    return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
    );
}

export default function Signup() {
    const { signup, loginWithGoogle } = useAuth();
    const [form, setForm] = useState({ name: "", email: "", password: "", currency: "USD" });
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [googleBusy, setGoogleBusy] = useState(false);
    const [verifyPending, setVerifyPending] = useState(false);
    const [signedUpEmail, setSignedUpEmail] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || form.password.length < 6) {
            toast.error("Fill all fields. Password must be 6+ chars."); return;
        }
        try {
            setBusy(true);
            const data = await signup(form);
            // If email confirmation is required, Supabase returns session=null
            if (!data.session) {
                setSignedUpEmail(form.email);
                setVerifyPending(true);
            }
        } catch (err) {
            toast.error(err.message || "Sign up failed");
        } finally { setBusy(false); }
    };

    const handleGoogle = async () => {
        try {
            setGoogleBusy(true);
            await loginWithGoogle();
        } catch (err) {
            toast.error(err.message || "Google sign-in failed");
            setGoogleBusy(false);
        }
    };

    if (verifyPending) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-md w-full card-elev rounded-3xl p-10 text-center space-y-5">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 grid place-items-center mx-auto">
                        <Mail className="w-8 h-8" />
                    </div>
                    <h1 className="font-display text-2xl font-extrabold text-slate-900">Check your email</h1>
                    <p className="text-slate-500 text-sm leading-relaxed">
                        We sent a verification link to <span className="font-semibold text-slate-700">{signedUpEmail}</span>.
                        Click the link to activate your account.
                    </p>
                    <div className="flex items-start gap-2.5 bg-emerald-50 rounded-xl p-3.5 text-left">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-emerald-700">Check your spam folder if you don't see it within a minute.</p>
                    </div>
                    <Link to="/login" className="block text-sm text-indigo-600 hover:text-indigo-700 font-semibold">
                        Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white order-2 lg:order-1">
                {/* Plain <a>, not <Link> — "/" is the static marketing landing page served
                    directly by nginx (frontend/public/landing.html), not an app route, so
                    this needs a full page navigation rather than client-side routing. */}
                <a href="/" className="flex items-center gap-2.5 mb-10">
                    <span className="w-10 h-10 rounded-xl gradient-indigo grid place-items-center text-white">
                        <Wallet className="w-5 h-5" />
                    </span>
                    <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">BudgetWise</span>
                </a>

                <div className="max-w-md w-full">
                    <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-2">Create your account</h1>
                    <p className="text-slate-500 mb-8">Track expenses, subscriptions, and savings — beautifully.</p>

                    {/* Google OAuth */}
                    <Button type="button" onClick={handleGoogle} disabled={googleBusy}
                        variant="outline"
                        className="w-full h-12 rounded-xl border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 mb-4 gap-2.5">
                        <GoogleIcon />
                        {googleBusy ? "Redirecting…" : "Continue with Google"}
                    </Button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs text-slate-400 font-medium">or sign up with email</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Full name</Label>
                            <Input data-testid="signup-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Alex Morgan" className="h-12 rounded-xl" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Email</Label>
                            <Input data-testid="signup-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 rounded-xl" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Password</Label>
                            <div className="relative">
                                <Input data-testid="signup-password" type={show ? "text" : "password"}
                                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                                    placeholder="6+ characters" className="h-12 rounded-xl pr-11" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Currency</Label>
                            <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                                <SelectTrigger data-testid="signup-currency" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="USD">USD ($) — US Dollar</SelectItem>
                                    <SelectItem value="INR">INR (₹) — Indian Rupee</SelectItem>
                                    <SelectItem value="EUR">EUR (€) — Euro</SelectItem>
                                    <SelectItem value="GBP">GBP (£) — British Pound</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button type="submit" disabled={busy} data-testid="signup-submit"
                            className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold">
                            {busy ? "Creating account…" : "Create account"}
                        </Button>
                    </form>

                    <p className="mt-8 text-sm text-slate-500 text-center">
                        Already have an account?{" "}
                        <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold" data-testid="goto-login">Sign in</Link>
                    </p>
                </div>
            </div>

            <div className="hidden lg:flex relative items-center justify-center bg-slate-50 gradient-mesh order-1 lg:order-2 p-10">
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.5)_1px,_transparent_0)] [background-size:24px_24px]" />
                <div className="relative max-w-md space-y-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Premium personal finance</p>
                    <h2 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                        Money that flows. <br />Decisions that matter.
                    </h2>
                    <p className="text-slate-600 leading-relaxed">
                        BudgetWise brings expenses, subscriptions, and savings goals into one breezy, beautiful dashboard.
                    </p>
                </div>
            </div>
        </div>
    );
}
